/**
 * Custom hook for copilot agent streaming.
 *
 * Enterprise UI behaviour:
 * - tool_call / tool_result are SUPPRESSED from chat (no tool spam)
 * - tool_call sets a streaming action label and increments tool-call count (for copy only)
 * - every SSE event touches streamingLastEventAt (idle / escalation copy in UI)
 * - query_result creates a workspace tab with structured data
 * - agent_message streams clean prose into chat
 * - sql_approval triggers the approval modal (also used for API/file requests)
 */

import { useCallback, useRef } from 'react';
import { useCopilotStore } from '../stores/useCopilotStore';
import { ChatStreamService } from '../../../services/chatStreamService';
import { useFetchThreadList } from './useCopilotHistory';
import type { QueryResultTab, ChartArtifact, FindingArtifact, MetricArtifact, CalculationAuditReport } from '../stores/useCopilotStore';
import { trackCopilotMessage, trackCopilotSQLApproval, trackCopilotSessionStart } from '../../../utils/analytics';
import { markProductStep } from '../../../hooks/useProductOnboarding';
import { getTenantIdFromStorage, setOnboardingFirstQIfEmpty } from '../../../utils/onboardingLocalStorage';

// Map tool names → user-friendly action labels for the streaming indicator
const TOOL_ACTION_LABELS: Record<string, string> = {
  explore_schema: 'Exploring your data…',
  propose_sql: 'Writing query…',
  execute_sql: 'Fetching data…',
  propose_chart: 'Building chart…',
  propose_metric_card: 'Building metric card…',
  search_context: 'Checking business context…',
  get_column_profile: 'Checking data…',
  save_finding: 'Saving finding…',
  analyze_data: 'Crunching numbers…',
  forecast_from_frame: 'Running forecast…',
  capture_definition: 'Learning definition…',
};

export function useCopilotStream() {
  const streamService = useRef(new ChatStreamService());
  const fetchThreads = useFetchThreadList();

  const sendMessage = useCallback(async (message: string, token: string) => {
    const tenantId = getTenantIdFromStorage();
    if (!tenantId) {
      useCopilotStore.getState().addMessage({
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Error: Not authenticated',
        timestamp: new Date().toISOString(),
      });
      return;
    }
    const store = useCopilotStore.getState();
    const threadId = store.threadId;

    const userMsgId = `user-${Date.now()}`;
    store.addMessage({
      id: userMsgId,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    });
    store.appendTimelineUser(userMsgId);

    store.setIsStreaming(true);
    store.setStreamingContent('');
    store.setStreamingAction('Thinking…');
    store.setInputText('');
    store.setPendingAuditId(null);
    if (store.messages.length <= 1) trackCopilotSessionStart(threadId);
    trackCopilotMessage(threadId);
    // Fire-and-forget: mark first_question_asked on first message sent
    markProductStep('first_question_asked');
    // Store first question for completion modal personalisation
    try {
      setOnboardingFirstQIfEmpty(getTenantIdFromStorage(), message);
    } catch { /* ignore */ }

    try {
      await streamService.current.sendMessage(
        message,
        threadId,
        token,
        tenantId,
        (event: any) => processEvent(event),
        undefined,
        'copilot',
      );
    } catch (error: any) {
      console.error('[useCopilotStream] Error:', error);
      const errId = `error-${Date.now()}`;
      useCopilotStore.getState().addMessage({
        id: errId,
        role: 'assistant',
        content: `Error: ${error.message || 'Unknown error occurred'}`,
        timestamp: new Date().toISOString(),
      });
      useCopilotStore.getState().appendTimelineAssistantMessage(errId);
    } finally {
      const latest = useCopilotStore.getState();
      const finalAuditId = latest.pendingAuditId;
      flushStreaming(finalAuditId);
      latest.setPendingAuditId(null);
      useCopilotStore.getState().setIsStreaming(false);
      fetchThreads();
    }
  }, [fetchThreads]);

  const sendSQLApproval = useCallback(
    async (
      action: 'approve' | 'edit' | 'reject',
      token: string,
      editedSql?: string,
      reason?: string,
    ) => {
      const store = useCopilotStore.getState();
      const threadId = store.threadId;
      const approval = store.pendingSQLApproval;

      if (!approval || !threadId) return;

      store.setPendingSQLApproval(null);
      store.setIsStreaming(true);
      // Context-aware action label
      if (action === 'reject') {
        store.setStreamingAction('Processing your feedback…');
        const ackId = `system-ack-${Date.now()}`;
        store.addMessage({
          id: ackId,
          role: 'assistant',
          content: reason
            ? `Noted - "${reason}". Let me adjust.`
            : 'Query rejected. Let me adjust.',
          timestamp: new Date().toISOString(),
        });
        store.appendTimelineAssistantMessage(ackId);
      } else if (action === 'edit') {
        store.setStreamingAction('Running edited query…');
      } else {
        store.setStreamingAction('Running query…');
      }
      trackCopilotSQLApproval(action, threadId);

      const resumeData = {
        type: 'sql_approval',
        session_id: approval.sessionId,
        response: {
          action,
          ...(action === 'edit' && editedSql ? { edited_sql: editedSql } : {}),
          ...(action === 'reject' && reason ? { reason } : {}),
        },
      };

      const tenantId = getTenantIdFromStorage();
      if (!tenantId) return;

      try {
        await streamService.current.sendMessage(
          '',
          threadId,
          token,
          tenantId,
          (event: any) => processEvent(event),
          resumeData,
          'copilot',
        );
      } catch (error: any) {
        console.error('[useCopilotStream] Approval error:', error);
        const eid = `error-${Date.now()}`;
        useCopilotStore.getState().addMessage({
          id: eid,
          role: 'assistant',
          content: `SQL approval error: ${error.message}`,
          timestamp: new Date().toISOString(),
        });
        useCopilotStore.getState().appendTimelineAssistantMessage(eid);
      } finally {
        const latest = useCopilotStore.getState();
        const finalAuditId = latest.pendingAuditId;
        flushStreaming(finalAuditId);
        latest.setPendingAuditId(null);
        useCopilotStore.getState().setIsStreaming(false);
        fetchThreads();
      }
    },
    [fetchThreads],
  );

  const stopStream = useCallback(() => {
    streamService.current.stop();
    const store = useCopilotStore.getState();
    if (store.streamingContent) {
      const sid = `assistant-${Date.now()}`;
      store.addMessage({
        id: sid,
        role: 'assistant',
        content: store.streamingContent + '\n\n*(Stopped by analyst)*',
        timestamp: new Date().toISOString(),
        auditId: store.pendingAuditId || undefined,
      });
      store.finalizeTimelineAssistant(sid);
      store.setStreamingContent('');
    } else {
      store.popTimelineStreamingIfStuck();
    }
    store.setPendingAuditId(null);
    store.setIsStreaming(false);
  }, []);

  return { sendMessage, sendSQLApproval, stopStream };
}

// ============================================================================
// Helpers
// ============================================================================

function flushStreaming(auditId?: string | null) {
  const store = useCopilotStore.getState();
  if (store.streamingContent) {
    const mid = `assistant-${Date.now()}`;
    store.addMessage({
      id: mid,
      role: 'assistant',
      content: store.streamingContent,
      timestamp: new Date().toISOString(),
      auditId: auditId || undefined,
    });
    store.finalizeTimelineAssistant(mid);
    store.setStreamingContent('');
  } else {
    store.popTimelineStreamingIfStuck();
  }
}

// ============================================================================
// Event processor - maps SSE events to store updates
// ============================================================================

function processEvent(event: any) {
  const store = useCopilotStore.getState();
  if (store.isStreaming) {
    store.touchStreamingActivity();
  }

  switch (event.type) {
    case 'agent_message': {
      const content = event.data?.content || '';
      if (content) {
        store.setStreamingAction('');
        store.ensureTimelineStreamingAssistant();
        store.appendStreamingContent(content);
      }
      break;
    }

    case 'tool_call': {
      // SUPPRESSED from chat - action label + cumulative activity list
      const toolName: string = event.data?.tool_name || '';
      const label = TOOL_ACTION_LABELS[toolName] || 'Working…';
      store.setStreamingAction(label);
      store.noteStreamingToolCall();
      break;
    }

    case 'tool_result': {
      // SUPPRESSED from chat - tool results are not shown
      // (query_result events handle workspace DataGrid separately)
      break;
    }

    case 'query_result': {
      // Structured query result → create a workspace tab.
      const d = event.data || {};
      const tab: QueryResultTab = {
        id: `qr-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: 'query_result',
        title: d.title || 'Query Result',
        columns: d.columns || [],
        rows: d.rows || [],
        rowCount: d.row_count || 0,
        elapsedMs: d.elapsed_ms || 0,
        sql: d.sql || '',
        connectorId: d.connector_id || '',
        connectorName: d.connector_name || '',
        connectorType: d.connector_type || '',
        connectorCategory: d.connector_category || '',
        isFileConnector: d.is_file_connector || false,
        truncated: d.truncated || false,
        createdAt: new Date().toISOString(),
      };
      store.addWorkspaceTab(tab);
      store.appendTimelineArtifact(tab.id);
      store.setStreamingAction('Analyzing results…');
      break;
    }

    case 'chart': {
      // Chart artifact → add to workspace as a ChartArtifact
      const c = event.data || {};
      const chartArtifact: ChartArtifact = {
        id: `chart-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: 'chart',
        title: c.title || 'Chart',
        chartType: c.chart_intent?.type || c.type || 'bar',
        spec: c.spec || {},
        rowCount: c.row_count || 0,
        columns: c.columns || [],
        chartIntent: c.chart_intent || {},
        createdAt: new Date().toISOString(),
        eventId: event.event_id || undefined,
      };
      store.addWorkspaceTab(chartArtifact);
      store.appendTimelineArtifact(chartArtifact.id);
      store.setStreamingAction('');
      break;
    }

    case 'forecast': {
      store.setStreamingAction('');
      break;
    }

    case 'metric_card': {
      const m = event.data || {};
      const metricArtifact: MetricArtifact = {
        id: `metric-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: 'metric_card',
        title: m.title || m.metric_name || 'Metric',
        metricName: m.metric_name || m.title || '',
        metricValue: m.metric_value,
        columns: m.columns || [],
        rowCount: m.row_count || 0,
        createdAt: new Date().toISOString(),
        eventId: event.event_id || undefined,
      };
      store.addWorkspaceTab(metricArtifact);
      store.appendTimelineArtifact(metricArtifact.id);
      store.setStreamingAction('');
      break;
    }

    case 'schema_loaded': {
      // Populate the Data Explorer schema tree (map snake_case → camelCase)
      const rawConnectors = event.data?.connectors || [];
      const mapped = rawConnectors.map((c: any) => ({
        connectorId: c.connector_id || c.connectorId || '',
        name: c.name || '',
        type: c.type || '',
        tables: (c.tables || []).map((t: any) => ({
          name: t.name || '',
          columns: (t.columns || []).map((col: any) =>
            typeof col === 'string' ? { name: col, type: '' } : { name: col.name || '', type: col.type || '' }
          ),
          rowCount: t.row_count || t.rowCount,
        })),
      }));
      store.setSchemaTree(mapped);
      break;
    }

    case 'suggested_title': {
      const title = event.title || '';
      if (title) {
        store.setThreadTitle(title);
      }
      break;
    }

    case 'sql_approval': {
      // Flush streaming content, show inline workspace approval card
      flushStreaming();
      const sqlAprId = `sql-apr-${Date.now()}`;
      store.appendTimelineSqlApproval(sqlAprId);
      store.setPendingSQLApproval({
        sql: event.data?.sql || '',
        title: event.data?.title || 'Query',
        connectorId: event.data?.connector_id || '',
        connectorName: event.data?.connector_name || '',
        connectorType: event.data?.connector_type || '',
        connectorCategory: event.data?.connector_category || '',
        sessionId: event.session_id,
        isFileConnector: event.data?.is_file_connector || false,
        isApiConnector: event.data?.connector_category === 'api',
        entityId: event.data?.entity_id || '',
        entityName: event.data?.entity_name || '',
        columns: event.data?.columns || [],
        resource: event.data?.resource || '',
        properties: event.data?.properties || [],
        limit: event.data?.limit ?? null,
        timelineId: sqlAprId,
      });
      store.setIsStreaming(false);
      break;
    }

    case 'finding': {
      const f = event.data || {};
      store.addFinding({
        id: f.id || `finding-${Date.now()}`,
        title: f.title || 'Untitled Finding',
        narrative: f.narrative || '',
        dataPoint: f.data_point || {},
        artifactRefs: f.artifact_refs || [],
        createdAt: f.created_at || new Date().toISOString(),
      });
      store.setStreamingAction('');
      break;
    }

    case 'knowledge_captured': {
      flushStreaming();
      const rawData = event.data;
      let kcNodes: Array<{ name: string; node_type: string; definition?: string; sql?: string }> = [];
      if (Array.isArray(rawData)) {
        kcNodes = rawData;
      } else if (rawData && typeof rawData === 'object') {
        // Could be {name, node_type, ...} or {items: [...]} or {data: {name, ...}}
        if (rawData.items && Array.isArray(rawData.items)) {
          kcNodes = rawData.items;
        } else if (rawData.name) {
          kcNodes = [rawData];
        } else if (rawData.data && rawData.data.name) {
          kcNodes = [rawData.data];
        }
      }

      if (kcNodes.length > 0) {
        const kcId = `knowledge-${Date.now()}`;
        store.addMessage({
          id: kcId,
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          type: 'knowledge_captured',
          knowledgeItems: kcNodes.map((kc) => ({
            name: kc.name,
            node_type: kc.node_type,
            definition: kc.definition,
            sql: kc.sql,
          })),
        });
        store.appendTimelineAssistantMessage(kcId);
      }
      break;
    }

    case 'context_usage': {
      const cu = event.data || {};
      store.setContextUsage({
        used: cu.used || 0,
        budget: cu.budget || 120000,
        pct: cu.pct || 0,
        trimmed: cu.trimmed || false,
        trimmedCount: cu.trimmed_count || 0,
      });
      break;
    }

    case 'turn_complete': {
      const auditId = store.pendingAuditId;
      flushStreaming(auditId);
      store.setPendingAuditId(null);
      store.setIsStreaming(false);
      break;
    }

    case 'calculation_audit': {
      const raw = event.data || {};
      const reportId = event.event_id || raw.id || `audit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const report: CalculationAuditReport = {
        id: reportId,
        threadId: raw.thread_id || store.threadId,
        question: raw.question || '',
        startedAt: raw.started_at || event.timestamp || new Date().toISOString(),
        completedAt: raw.completed_at || event.timestamp || new Date().toISOString(),
        showAuditLink: typeof raw.show_audit_link === 'boolean' ? raw.show_audit_link : undefined,
        dataSources: (raw.data_sources || []).map((s: any) => ({
          frameId: s.frame_id || '',
          title: s.title || 'Query Result',
          connectorId: s.connector_id || '',
          connectorName: s.connector_name || '',
          connectorType: s.connector_type || '',
          rowCount: s.row_count || 0,
          columns: s.columns || [],
          sql: s.sql || '',
          timestamp: s.timestamp || event.timestamp || new Date().toISOString(),
        })),
        steps: (raw.steps || []).map((st: any) => ({
          type: st.type || '',
          toolName: st.tool_name || '',
          toolArgs: st.tool_args || {},
          summary: st.summary || '',
          timestamp: st.timestamp || event.timestamp || new Date().toISOString(),
        })),
        charts: (raw.charts || []).map((c: any) => ({
          title: c.title || 'Chart',
          chartType: c.chart_type || 'chart',
          rowCount: c.row_count || 0,
          columns: c.columns || [],
          timestamp: c.timestamp || event.timestamp || new Date().toISOString(),
        })),
        story: raw.story
          ? {
              headline: raw.story.headline || '',
              dataSources: raw.story.data_sources
                ? {
                    summary: raw.story.data_sources.summary || '',
                    connectors: raw.story.data_sources.connectors || [],
                    tablesOrEndpoints: raw.story.data_sources.tables_or_endpoints || [],
                    details: (raw.story.data_sources.details || []).map((d: any) => ({
                      name: d.name || '',
                      connectorType: d.connector_type || '',
                      records: d.records || 0,
                      columns: d.columns || 0,
                      frameId: d.frame_id || '',
                      query: d.query || '',
                      requestType: d.request_type || 'sql',
                      tablesOrEndpoints: d.tables_or_endpoints || [],
                    })),
                  }
                : undefined,
              staging: raw.story.staging
                ? {
                    summary: raw.story.staging.summary || '',
                    stagedRecords: raw.story.staging.staged_records || 0,
                    frameCount: raw.story.staging.frame_count || 0,
                  }
                : undefined,
              calculation: raw.story.calculation
                ? {
                    summary: raw.story.calculation.summary || '',
                    sqlStepCount: raw.story.calculation.sql_step_count || 0,
                    finalSql: raw.story.calculation.final_sql || '',
                    steps: (raw.story.calculation.steps || []).map((step: any) => ({
                      tool: step.tool || '',
                      sql: step.sql || '',
                    })),
                  }
                : undefined,
              outputs: raw.story.outputs
                ? {
                    summary: raw.story.outputs.summary || '',
                    resultTypes: raw.story.outputs.result_types || [],
                    chartCount: raw.story.outputs.chart_count || 0,
                    chartTitles: raw.story.outputs.chart_titles || [],
                    chartDetails: (raw.story.outputs.chart_details || []).map((c: any) => ({
                      title: c.title || '',
                      type: c.type || '',
                      records: c.records || 0,
                      columns: c.columns || [],
                      xFields: c.x_fields || [],
                      yFields: c.y_fields || [],
                      timeUnit: c.time_unit || '',
                    })),
                    forecastCount: raw.story.outputs.forecast_count ?? 0,
                    forecastDetails: (raw.story.outputs.forecast_details || []).map((f: any) => ({
                      title: f.title || '',
                      frameId: f.frame_id || '',
                      selectedModel: f.selected_model || '',
                      horizon: f.horizon ?? 0,
                      timeUnit: f.time_unit || '',
                      confidenceLevel: f.confidence_level,
                      confidenceLevelPct: f.confidence_level_pct,
                      reliability: f.reliability || '',
                      historyPoints: f.history_points ?? 0,
                      targetColumn: f.target_column || '',
                      timeColumn: f.time_column || '',
                      periodsCount: f.periods_count ?? 0,
                      firstPeriod: f.first_period
                        ? {
                            period: f.first_period.period,
                            prediction: f.first_period.prediction,
                            lower: f.first_period.lower,
                            upper: f.first_period.upper,
                          }
                        : undefined,
                      candidateModels: (f.candidate_models || []).map((m: any) => ({
                        model: m.model || '',
                        mae: m.mae ?? null,
                        rmse: m.rmse ?? null,
                        wape: m.wape ?? null,
                        p95AbsError: m.p95_abs_error ?? null,
                        selectionScore: m.selection_score ?? null,
                      })),
                      selectedModelMetrics: f.selected_model_metrics
                        ? {
                            mae: f.selected_model_metrics.mae ?? null,
                            rmse: f.selected_model_metrics.rmse ?? null,
                            wape: f.selected_model_metrics.wape ?? null,
                            p95AbsError: f.selected_model_metrics.p95_abs_error ?? null,
                            selectionScore: f.selected_model_metrics.selection_score ?? null,
                          }
                        : undefined,
                      caveats: f.caveats || [],
                      dataWindowStart: f.data_window_start || '',
                      dataWindowEnd: f.data_window_end || '',
                      droppedPoints: f.dropped_points ?? 0,
                      rawRows: f.raw_rows ?? 0,
                      preparedPoints: f.prepared_points ?? 0,
                      seasonLength: f.season_length ?? 0,
                    })),
                  }
                : undefined,
            }
          : undefined,
      };
      const storedId = store.addAuditReport(report, reportId);
      store.setPendingAuditId(storedId);
      break;
    }

    case 'error': {
      const eid = `error-${Date.now()}`;
      store.addMessage({
        id: eid,
        role: 'assistant',
        content: `⚠️ ${event.message || 'An error occurred'}`,
        timestamp: new Date().toISOString(),
      });
      store.appendTimelineAssistantMessage(eid);
      store.setIsStreaming(false);
      break;
    }

    default:
      console.log('[useCopilotStream] Unhandled event:', event.type, event);
  }
}
